import React, { useState, useEffect } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from "swiper/modules";
import 'swiper/css';
import { Link } from 'react-router-dom';

const TourItem = ({ image, title, link, rating, price, duration, bookLink }) => (
    <div className="tour-box th-ani gsap-cursor">
        <div className="tour-box_img global-img">
            <img src={image} alt={title} />
        </div>
        <div className="tour-content">
            <h3 className="box-title">
                <Link to={link}>{title}</Link>
            </h3>
            <div className="tour-rating">
                <div className="star-rating" role="img" aria-label={`Rated ${rating} out of 5`}>
                    <span style={{ width: `${rating * 20}%` }}>
                        Rated <strong className="rating">{rating}</strong> out of 5
                    </span>
                </div>
                <Link to={link} className="woocommerce-review-link">
                    (<span className="count">{rating}</span> Rating)
                </Link>
            </div>
            <h4 className="tour-box_price">
                <span className="currency">${price}</span>/Person
            </h4>
            <div className="tour-action">
                <span>
                    <i className="fa-light fa-clock" /> {duration} Days
                </span>
                <Link to={bookLink} className="th-btn style4 th-icon">
                    Book Now
                </Link>
            </div>
        </div>
    </div>
);

function TourTwo() {
    const [activeTab, setActiveTab] = useState('nav-step1');
    const [tourSectionData, setTourSectionData] = useState({
        subtitle: 'Best Experience',
        title: 'Amazing Travel Experience',
        description: '',
        background_image_url: null,
        show_tour_packages: true,
        tour_tab_label: 'Tour Package',
        show_hotels: true,
        hotel_tab_label: 'Hotel',
        show_transport: true,
        transport_tab_label: 'Transport'
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [tourPackages, setTourPackages] = useState({
        vacation: [], // For Tour Package tab
        business: [], // For Hotel tab  
        group: []     // For Transport tab
    });

    // Fetch tour section data from API
    useEffect(() => {
        const fetchTourSectionData = async () => {
            setLoading(true);
            try {
                const response = await fetch('https://dzsoftech.net/tours/api/airticket/tour-section/');
                if (!response.ok) {
                    throw new Error('Failed to fetch tour section data');
                }
                const data = await response.json();
                
                // Use the first active tour section data if available
                if (data.results && data.results.length > 0) {
                    const sectionData = data.results[0];
                    const newSectionData = {
                        subtitle: sectionData.subtitle || 'Best Experience',
                        title: sectionData.title || 'Amazing Travel Experience',
                        description: sectionData.description || '',
                        background_image_url: sectionData.background_image_url,
                        show_tour_packages: sectionData.show_tour_packages,
                        tour_tab_label: sectionData.tour_tab_label || 'Tour Package',
                        show_hotels: sectionData.show_hotels,
                        hotel_tab_label: sectionData.hotel_tab_label || 'Hotel',
                        show_transport: sectionData.show_transport,
                        transport_tab_label: sectionData.transport_tab_label || 'Transport'
                    };
                    setTourSectionData(newSectionData);
                    
                    // Set the active tab to the first visible tab
                    if (newSectionData.show_tour_packages) {
                        setActiveTab('nav-step1');
                    } else if (newSectionData.show_hotels) {
                        setActiveTab('nav-step2');
                    } else if (newSectionData.show_transport) {
                        setActiveTab('nav-step3');
                    }
                }
            } catch (err) {
                console.error('Error fetching tour section data:', err);
                setError(err.message);
                // Keep default values on error
            } finally {
                setLoading(false);
            }
        };

        fetchTourSectionData();
    }, []);

    // Fetch tour packages from API
    useEffect(() => {
        const fetchTourPackages = async () => {
            try {
                const response = await fetch('https://dzsoftech.net/tours/api/airticket/tours/');
                if (!response.ok) {
                    throw new Error('Failed to fetch tour packages');
                }
                const data = await response.json();
                
                if (data.results && data.results.length > 0) {
                    // Group tours by type
                    const groupedTours = {
                        vacation: [], // For Tour Package tab
                        business: [], // For Hotel tab  
                        group: []     // For Transport tab
                    };
                    
                    data.results.forEach(tour => {
                        if (tour.is_active && groupedTours[tour.tour_type]) {
                            groupedTours[tour.tour_type].push(tour);
                        }
                    });
                    
                    setTourPackages(groupedTours);
                }
            } catch (err) {
                console.error('Error fetching tour packages:', err);
                // Keep empty arrays on error
            }
        };

        fetchTourPackages();
    }, []);

    // Function to get default image based on tour name
    const getDefaultImage = (tourName, index = 0) => {
        const imageMap = {
            'greece': '/assets/img/tour/tour_box_1.jpg',
            'italy': '/assets/img/tour/tour_box_2.jpg', 
            'dubai': '/assets/img/tour/tour_box_3.jpg',
            'paris': '/assets/img/tour/tour_box_4.jpg',
            'plaza': '/assets/img/tour/tour_box_5.jpg',
            'hotel': '/assets/img/tour/tour_box_6.jpg',
            'caravan': '/assets/img/tour/tour_box_13.jpg',
            'coach': '/assets/img/tour/tour_box_14.jpg'
        };
        
        const name = tourName.toLowerCase();
        for (const [key, imagePath] of Object.entries(imageMap)) {
            if (name.includes(key)) {
                return imagePath;
            }
        }
        
        // Fallback to generic images based on index
        const fallbacks = [
            '/assets/img/tour/tour_box_1.jpg',
            '/assets/img/tour/tour_box_2.jpg',
            '/assets/img/tour/tour_box_3.jpg',
            '/assets/img/tour/tour_box_4.jpg'
        ];
        return fallbacks[index % fallbacks.length];
    };

    // Function to render tour slides for a specific type
    const renderTourSlides = (tours, fallbackTours = []) => {
        const toursToRender = tours.length > 0 ? tours : fallbackTours;
        
        return toursToRender.map((tour, index) => (
            <SwiperSlide key={tour.id || `fallback-${index}`}>
                <TourItem
                    image={tour.main_image || getDefaultImage(tour.name, index)}
                    title={tour.name}
                    link="/tour-details"
                    rating={4.8 + (index * 0.1)} // Dynamic rating
                    price={Math.round(tour.price)}
                    duration={tour.duration_days}
                    bookLink="/contact"
                />
            </SwiperSlide>
        ));
    };

    return (
        <section
            className="tour-area3 position-relative bg-top-center overflow-hidden space"
            id="service-sec"
            style={{ 
                backgroundImage: tourSectionData.background_image_url 
                    ? `url('${tourSectionData.background_image_url}')` 
                    : "url('/assets/img/bg/tour_bg_1.jpg')", 
                backgroundRepeat: "no-repeat" 
            }}
        >
            <div className="container">
                <div className="row justify-content-center">
                    <div className="col-lg-8">
                        <div className="title-area text-center">
                            <span className="sub-title">{tourSectionData.subtitle}</span>
                            <h2 className="sec-title">{tourSectionData.title}</h2>
                            {tourSectionData.description && (
                                <p className="sec-desc">{tourSectionData.description}</p>
                            )}
                        </div>
                    </div>
                </div>
                <div className="nav nav-tabs tour-tabs" id="nav-tab" role="tablist">
                    {tourSectionData.show_tour_packages && (
                        <button
                            className={`nav-link th-btn ${activeTab === 'nav-step1' ? 'active' : ''}`}
                            id="nav-step1-tab"
                            data-bs-toggle="tab"
                            data-bs-target="#nav-step1"
                            type="button"
                            onClick={() => setActiveTab('nav-step1')}
                        >
                            <img src="/assets/img/icon/tour_icon_1.svg" alt="" />
                            {tourSectionData.tour_tab_label}
                        </button>
                    )}
                    {tourSectionData.show_hotels && (
                        <button
                            className={`nav-link th-btn ${activeTab === 'nav-step2' ? 'active' : ''}`}
                            id="nav-step2-tab"
                            data-bs-toggle="tab"
                            data-bs-target="#nav-step2"
                            type="button"
                            onClick={() => setActiveTab('nav-step2')}
                        >
                            <img src="/assets/img/icon/tour_icon_2.svg" alt="" />
                            {tourSectionData.hotel_tab_label}
                        </button>
                    )}
                    {tourSectionData.show_transport && (
                        <button
                            className={`nav-link th-btn ${activeTab === 'nav-step3' ? 'active' : ''}`}
                            id="nav-step3-tab"
                            data-bs-toggle="tab"
                            data-bs-target="#nav-step3"
                            type="button"
                            onClick={() => setActiveTab('nav-step3')}
                        >
                            <img src="/assets/img/icon/tour_icon_3.svg" alt="" />
                            {tourSectionData.transport_tab_label}
                        </button>
                    )}
                </div>
                <div className="tab-content" id="nav-tabContent">
                    <div className={`tab-pane fade ${activeTab === 'nav-step1' ? 'show active' : ''}`} id="nav-step1" role="tabpanel">
                        <div className="slider-area tour-slider slider-drag-wrap">
                            <Swiper
                                modules={[Pagination]}
                                spaceBetween={24}
                                slidesPerView={4}
                                loop={false}
                                breakpoints={{
                                    0: { slidesPerView: 1 },
                                    576: { slidesPerView: 1 },
                                    768: { slidesPerView: 2 },
                                    992: { slidesPerView: 3 },
                                    1200: { slidesPerView: 3 },
                                    1400: { slidesPerView: 4 },
                                }}
                                pagination={{
                                    clickable: true,
                                }}
                                className="swiper th-slider has-shadow"
                            >
                                {renderTourSlides(tourPackages.vacation, [
                                    { id: 'fallback-1', name: 'Greece Tour Package', price: 980, duration_days: 7, main_image: null },
                                    { id: 'fallback-2', name: 'Italy Tour Package', price: 980, duration_days: 7, main_image: null },
                                    { id: 'fallback-3', name: 'Dubai Tour Package', price: 980, duration_days: 7, main_image: null },
                                    { id: 'fallback-4', name: 'Paris Tour Package', price: 980, duration_days: 7, main_image: null }
                                ])}
                            </Swiper>
                        </div>
                    </div>
                    <div className={`tab-pane fade ${activeTab === 'nav-step2' ? 'show active' : ''}`} id="nav-step2" role="tabpanel">
                        <div className="slider-area tour-slider slider-drag-wrap">
                            <Swiper
                                modules={[Pagination]}
                                spaceBetween={24}
                                slidesPerView={4}
                                loop={false}
                                breakpoints={{
                                    576: { slidesPerView: 1 },
                                    768: { slidesPerView: 2 },
                                    992: { slidesPerView: 3 },
                                    1200: { slidesPerView: 4 },
                                }}
                                pagination={{
                                    clickable: true,
                                }}
                                className="swiper th-slider has-shadow"
                            >
                                {renderTourSlides(tourPackages.business, [
                                    { id: 'fallback-5', name: 'The Plaza, New York', price: 980, duration_days: 7, main_image: null },
                                    { id: 'fallback-6', name: 'Grand Hotel Package', price: 1200, duration_days: 5, main_image: null },
                                    { id: 'fallback-7', name: 'Business Hotel Stay', price: 850, duration_days: 3, main_image: null },
                                    { id: 'fallback-8', name: 'Premium Hotel Deal', price: 1150, duration_days: 4, main_image: null }
                                ])}
                            </Swiper>
                        </div>
                    </div>
                    <div className={`tab-pane fade ${activeTab === 'nav-step3' ? 'show active' : ''}`} id="nav-step3" role="tabpanel">
                        <div className="slider-area tour-slider slider-drag-wrap">
                            <Swiper
                                modules={[Pagination]}
                                spaceBetween={24}
                                slidesPerView={4}
                                loop={false}
                                breakpoints={{
                                    576: { slidesPerView: 1 },
                                    768: { slidesPerView: 2 },
                                    992: { slidesPerView: 3 },
                                    1200: { slidesPerView: 4 },
                                }}
                                pagination={{
                                    clickable: true,
                                }}
                                className="swiper th-slider has-shadow"
                            >
                                {renderTourSlides(tourPackages.group, [
                                    { id: 'fallback-9', name: 'Caravan Trip Package', price: 980, duration_days: 7, main_image: null },
                                    { id: 'fallback-10', name: 'Coach Bus Tour', price: 750, duration_days: 5, main_image: null },
                                    { id: 'fallback-11', name: 'Group Transport Deal', price: 650, duration_days: 3, main_image: null },
                                    { id: 'fallback-12', name: 'Premium Coach Service', price: 1100, duration_days: 6, main_image: null }
                                ])}
                            </Swiper>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

export default TourTwo;
